import { supabase } from '../lib/supabaseClient';

/**
 * Email Verification Service
 * Handles email verification codes with 10-minute expiration
 */

// Base URL for backend API (used for SendGrid email endpoint)
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'https://noorcad-api.vercel.app').replace(/\/$/, '');

// Generate a random 6-digit verification code
export const generateVerificationCode = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Create and store a verification code
export const createVerificationCode = async (email: string): Promise<{ success: boolean; code?: string; error?: string }> => {
  try {
    const code = generateVerificationCode();
    const expiresAt = new Date(Date.now() + 2 * 60 * 1000); // 2 minutes from now

    const { error } = await supabase
      .from('email_verification_codes')
      .insert({
        email,
        code,
        expires_at: expiresAt.toISOString(),
        verified: false
      });

    if (error) {
      console.error('Error creating verification code:', error);
      return { success: false, error: error.message };
    }

    return { success: true, code };
  } catch (error) {
    console.error('Unexpected error creating verification code:', error);
    return { success: false, error: 'Failed to create verification code' };
  }
};

// Verify a code
export const verifyCode = async (email: string, code: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get the verification code
    const { data, error: fetchError } = await supabase
      .from('email_verification_codes')
      .select('*')
      .eq('email', email)
      .eq('code', code)
      .eq('verified', false)
      .single();

    if (fetchError || !data) {
      return { success: false, error: 'Invalid verification code' };
    }

    // Check if code is expired
    const expiresAt = new Date(data.expires_at);
    if (expiresAt < new Date()) {
      return { success: false, error: 'Verification code has expired. Please request a new one.' };
    }

    // Mark code as verified
    const { error: updateError } = await supabase
      .from('email_verification_codes')
      .update({
        verified: true,
        verified_at: new Date().toISOString()
      })
      .eq('id', data.id);

    if (updateError) {
      console.error('Error updating verification code:', updateError);
      return { success: false, error: 'Failed to verify code' };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error verifying code:', error);
    return { success: false, error: 'Failed to verify code' };
  }
};

// Resend verification code (invalidate old ones and create new)
export const resendVerificationCode = async (email: string): Promise<{ success: boolean; code?: string; error?: string }> => {
  try {
    // Invalidate all previous codes for this email
    await supabase
      .from('email_verification_codes')
      .update({ verified: true }) // Mark as verified to prevent reuse
      .eq('email', email)
      .eq('verified', false);

    // Create new code
    return await createVerificationCode(email);
  } catch (error) {
    console.error('Unexpected error resending verification code:', error);
    return { success: false, error: 'Failed to resend verification code' };
  }
};

// Send verification email via backend (which uses SendGrid)
export const sendVerificationEmail = async (
  email: string,
  code: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/send-verification-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success) {
      const errorMessage = data?.error || 'Failed to send verification email';
      console.error('Verification email error:', errorMessage);
      return { success: false, error: errorMessage };
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending verification email:', error);
    return { success: false, error: 'Failed to send verification email' };
  }
};

// Send password reset email via backend (which uses SendGrid)
export const sendPasswordResetEmail = async (
  email: string,
  code: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/send-password-reset-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success) {
      const errorMessage = data?.error || 'Failed to send password reset email';
      console.error('Password reset email error:', errorMessage);
      return { success: false, error: errorMessage };
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    return { success: false, error: 'Failed to send password reset email' };
  }
};

// Update user password via server admin API (bypasses Supabase recovery email)
export const updatePasswordViaServer = async (
  email: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/admin-update-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, newPassword }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success) {
      // Prefer data.error, fallback to data.details if more specific, then generic message
      const errorMessage = data?.error || data?.details || 'Failed to update password';
      console.error('Password update error:', errorMessage);
      return { success: false, error: errorMessage };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating password:', error);
    return { success: false, error: 'Failed to update password' };
  }
};
